/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { ICON_CHECK_CIRCLE, ICON_CLOSE, ICON_INFO, ICON_REPORT, ICON_WARNING } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { SdsTransition, SdsTransitionProps, TRANSITION_TYPE_SLIDE_FROM_TOP_FADE } from '../SdsTransition';
import { SPINNER_TYPE_SMALL, Spinner } from '../Spinner';
import { ToastProps, toastDefaults } from './Toast.props';
import {
  TOAST_TYPE_DANGER,
  TOAST_TYPE_INFO,
  TOAST_TYPE_LOADING,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from './Toast.types';

/**
 * SDS Toast component.
 */
export const Toast = (props: ToastProps): JSX.Element => {
  const {
    autoCloseDelay,
    classes,
    customIcon,
    showIcon,
    showToast,
    stateHandler,
    title,
    toastContent,
    toastType,
    ...rest
  } = {
    ...toastDefaults,
    ...props,
  };

  const { activeToast, updateActiveToast } = stateHandler();
  const [activeClass, setActiveClass] = useState<string>(``);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [removeGlobalToast, setRemoveGlobalToast] = useState<boolean>(false);
  const toastClass = clsx(`sds-toast`, `sds-toast-type--${toastType}`, classes);

  const hideToast = (): void => {
    setIsActive(false);
  };

  const closeHandler = (): void => {
    setActiveClass(``);
    setRemoveGlobalToast(true);
  };

  useEffect(() => {
    if (showToast) {
      setActiveClass(DXP_ACTIVE_CLASS);
      setIsActive(true);
    }
    if (toastType === TOAST_TYPE_LOADING) {
      return;
    }
    const autoClose = setTimeout(hideToast, autoCloseDelay);

    return (): void => {
      clearTimeout(autoClose);
    };
  }, [autoCloseDelay, showToast, toastType]);

  // Special effect call for managing global state.
  // When the toast has finished closing,
  // And the global toast still exists,
  // Set the global toast to null
  useEffect(() => {
    if (removeGlobalToast && activeToast) {
      updateActiveToast(null);
    }
  }, [activeToast, removeGlobalToast, updateActiveToast]);

  const getIcon = (): JSX.Element => {
    if (customIcon) {
      return customIcon;
    }

    switch (toastType) {
      case TOAST_TYPE_DANGER:
        return ICON_REPORT;
      case TOAST_TYPE_INFO:
        return ICON_INFO;
      case TOAST_TYPE_SUCCESS:
        return ICON_CHECK_CIRCLE;
      case TOAST_TYPE_WARNING:
        return ICON_WARNING;
      case TOAST_TYPE_LOADING:
        return <Spinner type={SPINNER_TYPE_SMALL} />;
      default:
        return <></>;
    }
  };

  const getRole = (): string => {
    switch (toastType) {
      case TOAST_TYPE_DANGER:
      case TOAST_TYPE_WARNING:
        return `alert`;
      default:
        return `status`;
    }
  };

  const toast = (
    <>
      {activeClass && (
        <div className="sds-toast__container" role={getRole()} {...rest}>
          <div className={toastClass}>
            {showIcon && (
              <div className="sds-toast-icon">
                <SdsIcon svg={getIcon()} />
              </div>
            )}
            <div className="sds-toast-content">
              {title && <span className="sds-toast-title">{title}</span>}
              {toastContent && <div className="sds-toast-message">{toastContent}</div>}
            </div>
            <button
              aria-label="Close"
              className="sds-toast-close"
              onClick={(event): void => {
                event.preventDefault();
                hideToast();
              }}
            >
              <SdsIcon svg={ICON_CLOSE} />
            </button>
          </div>
        </div>
      )}
    </>
  );

  const transitionProps = {
    exitCallback: closeHandler,
    isActive: isActive,
    transitionType: TRANSITION_TYPE_SLIDE_FROM_TOP_FADE,
  } as SdsTransitionProps;

  if (showToast) {
    return <SdsTransition {...transitionProps}>{toast}</SdsTransition>;
  }

  return <></>;
};
